import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { DayPerformance, DailySummary, StoredDayScoreInputs } from '@/types'

/** lb_users/{uid}/dailySummaries/{YYYY-MM-DD} — the week strip's and Progress's days. */
function summaryRef(uid: string, date: string) {
  return doc(db, 'lb_users', uid, 'dailySummaries', date)
}

/**
 * Mobile FirestoreService.updateDailySummary. `dayScore` carries the day
 * score's inputs (the day's ceilings + alcohol / energy-drink flags, spec §4a)
 * so the Progress day grid can judge the day without its entries; without
 * `limits` (a past day) the merge keeps the stored ceilings.
 */
export async function writeDailySummary(
  uid: string,
  summary: DailySummary,
  performance: DayPerformance,
  totalItems: number,
  dayScore: StoredDayScoreInputs | Omit<StoredDayScoreInputs, 'limits'>,
): Promise<void> {
  await setDoc(
    summaryRef(uid, summary.date),
    {
      date: summary.date,
      totalItems,
      // Raw values + mobile's exact field names (context/DiaryContext.tsx
      // writeDailySummary) — phone and web merge into the SAME doc, so any
      // drift makes the two clients fight over it.
      calories: summary.totals.calories,
      addedSugar: summary.totals.addedSugarsG,
      saturatedFat: summary.totals.saturatedFatG,
      fiber: summary.totals.fiberG,
      sodium: summary.totals.sodiumMg || 0,
      protein: summary.totals.proteinG || 0,
      fluid: summary.totals.fluidMl,
      performance,
      dayScore,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
}

/**
 * Daily notes live on the SAME dailySummaries/{date} doc as the nutrient rollup
 * (field `dailyNotes`) — mirrors mobile FirestoreService.updateDailyNotes.
 * Always merge; never clobber the totals.
 */
export async function updateDailyNotes(uid: string, date: string, notes: string): Promise<void> {
  await setDoc(
    summaryRef(uid, date),
    { dailyNotes: notes, updatedAt: serverTimestamp() },
    { merge: true },
  )
}

/** Live dailyNotes for a date (real-time so phone edits appear on web). */
export function subscribeDailyNotes(
  uid: string,
  date: string,
  onUpdate: (notes: string) => void,
): () => void {
  return onSnapshot(summaryRef(uid, date), (snap) => {
    onUpdate(snap.exists() ? ((snap.data().dailyNotes as string) || '') : '')
  })
}
